import { AddCircleOutline, Delete, Description, Edit, RemoveRedEye } from '@mui/icons-material'
import { Box, Tooltip, useTheme } from '@mui/material'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import GenericTable from 'src/components/table/GenericTable'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { invalidateSchemaForm, useDeleteFormSchemaById, useGetAllFormSchema } from '../../../api/form-schema'
import { AppRoutesNavigation } from '../../../app-routes'
import { useCustomerCtx, useDialogCtx } from '../../../hooks/context-hooks'
import { openFormSchemaLayoutPreview } from 'src/screens/customer/form/formSchemaPreviewDialog'
import { SchemaType } from 'src/generated-types/form-schema'
import GenerateReportModal from 'src/components/report-templates/GenerateReportModal'
import { invalidateReportTemplate } from 'src/api/report-template'
import { isDynamicReportsEnabled } from 'src/feature-flags'

const FormSchemas = () => {
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { customer } = useCustomerCtx()
  const { data } = useGetAllFormSchema(customer?.id as EntityId, { schemaType: SchemaType.FormSchema })
  const mutate = useDeleteFormSchemaById(customer?.id as EntityId)
  const navigate = useNavigate()
  const theme = useTheme()
  const { t: translate } = useTranslation(['common', 'form-schema'])

  const handleGenerateReport = (formSchemaVersionId: number, formSchemaId: number, isFormSchemaActive: boolean) => {
    showDialog(
      GenerateReportModal,
      {
        componentProps: {
          customerId: customer?.id as EntityId,
          openedFromTemplateRow: false,
          formSchemaVersionId: formSchemaVersionId,
          formSchemaId: formSchemaId,
          isFormSchemaActive: isFormSchemaActive,
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  const STATIC_COLUMNS = [
    {
      field: 'name',
      headerName: translate('name', { ns: 'common' }),
      flex: 5,
    },
    {
      field: 'description',
      headerName: translate('description', { ns: 'common' }),
      flex: 5,
    },
    {
      field: 'isActive',
      headerName: translate('isActive', { ns: 'common' }),
      flex: 2,
      renderCell: params => (params.row.isActive ? 'Yes' : 'No'),
    },
    {
      field: 'actions',
      headerName: t('actions', { ns: 'common' }),
      flex: 2,
      renderCell: params => (
        <Box width={200} display={'flex'} flexDirection={'row'} justifyContent={'space-evenly'}>
          <Edit
            className={'cursor-pointer'}
            onClick={() => {
              navigate(AppRoutesNavigation.Customer.editFormSchema(customer?.id, params.row?.id))
            }}
          />
          {isDynamicReportsEnabled && (
            <Tooltip title={translate('generate-report', { ns: 'dynamic-report' }) as string}>
              <Description
                className={'cursor-pointer'}
                onClick={() => handleGenerateReport(params.row?.version?.id, params.row?.id, params.row?.isActive)}
              />
            </Tooltip>
          )}

          <RemoveRedEye
            className={'cursor-pointer'}
            onClick={() => {
              const formSchemaVersion = params?.row?.version
              openFormSchemaLayoutPreview(showDialog, {
                layout: formSchemaVersion?.layout,
                title: params?.row?.name,
              })
            }}
          />
          <Delete
            className={'cursor-pointer'}
            onClick={() => {
              showConfirmDialog(
                translate('delete-from-title', { ns: 'form-schema' }),
                translate('delete-from-content', { ns: 'form-schema' }),
                {
                  acceptText: translate('yes', { ns: 'common' }),
                  cancelText: translate('no', { ns: 'common' }),
                },
                async pop => {
                  await mutate.mutateAsync(params?.row?.id, {
                    onSuccess: () => {
                      toast.success(t('form-schema-deleted', { ns: 'form-schema' }))
                      invalidateSchemaForm.getAllFormSchema(customer?.id as EntityId)
                      invalidateReportTemplate.getFormSchemaReportTemplates(customer?.id as EntityId)
                      pop()
                    },
                    onError: () => {
                      toast.error(t('error-deleting-form-schema', { ns: 'form-schema' }))
                    },
                  })
                },
              )
            }}
          />
        </Box>
      ),
    },
  ]
  return (
    <CommonPageLayout
      titleSection={{ title: translate('form-schema', { ns: 'form-schema' }) }}
      topAction={[
        {
          buttonText: translate('form-schema', { ns: 'page-container' }),
          buttonIcon: <AddCircleOutline />,
          onClick: () => {
            navigate(AppRoutesNavigation.Customer.formGenerator(customer?.id))
          },
          variant: 'outlined',
          shadowWeight: null,
          border: 0,
        },
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GenericTable
          id="form-schemas"
          columns={STATIC_COLUMNS}
          data={data || []}
          autoHeight={false}
          height={window.innerHeight - 230 + 'px'}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default FormSchemas
